import { useState, useEffect, useCallback } from 'react';
import type { CookieConsent, CookieCategory } from '../types';

const STORAGE_KEY = 'cu_cookie_consent';

const defaultConsent: CookieConsent = {
  essential: true,
  statistics: false,
  marketing: false,
  timestamp: new Date().toISOString(),
};

export function useCookieConsent() {
  const [consent, setConsent] = useState<CookieConsent | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        setConsent(JSON.parse(stored));
      } catch {
        setConsent(null);
      }
    }
    setIsLoaded(true);
  }, []);

  const saveConsent = useCallback((newConsent: Partial<CookieConsent>) => {
    const fullConsent: CookieConsent = {
      ...defaultConsent,
      ...newConsent,
      essential: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(fullConsent));
    setConsent(fullConsent);
    
    // Dispatch event for other components
    window.dispatchEvent(new CustomEvent('cookieConsentChanged', { 
      detail: fullConsent 
    }));
  }, []);

  const acceptAll = useCallback(() => {
    saveConsent({
      essential: true,
      statistics: true,
      marketing: true,
    });
  }, [saveConsent]);

  const acceptEssentialOnly = useCallback(() => {
    saveConsent({
      essential: true,
      statistics: false,
      marketing: false,
    });
  }, [saveConsent]);

  const revokeConsent = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setConsent(null);
    window.dispatchEvent(new CustomEvent('cookieConsentChanged', { 
      detail: null 
    }));
  }, []);

  const hasConsent = useCallback((category: CookieCategory): boolean => {
    if (!consent) return category === 'essential';
    return consent[category] ?? false;
  }, [consent]);

  const exportConsentLog = useCallback(() => {
    const logs = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (logs) {
      const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify([logs], null, 2));
      const downloadAnchorNode = document.createElement('a');
      downloadAnchorNode.setAttribute('href', dataStr);
      downloadAnchorNode.setAttribute('download', 'cookie-consent-log.json');
      document.body.appendChild(downloadAnchorNode);
      downloadAnchorNode.click();
      downloadAnchorNode.remove();
    }
  }, []);

  return {
    consent,
    isLoaded,
    hasConsent,
    saveConsent,
    acceptAll,
    acceptEssentialOnly,
    revokeConsent,
    exportConsentLog,
    showBanner: isLoaded && !consent,
  };
}
